import { Box, Flex } from '@/components/atoms/Grid'
import { CheckIconFilledDefault } from '@/components/atoms/icons/CheckIconFilledDefaut'
import ResponsiveImage from '@/components/atoms/responsiveImage'
import { Small3, Tiny2 } from '@/components/atoms/typography'
import { applyImagePolicy } from '@/utils/imageExtension'
import { FC } from 'react'
import { Rating } from 'react-simple-star-rating'
import styled, { useTheme } from 'styled-components'
import AdditionalRatingInfo from './additionalRatingInfo'
import { useDomain } from '@/providers/storeProvider'
import { ProductRateRow } from '@/network/graphql.g'
import { TranslatedReviewsProps, originLanguageProps } from './reviews'
import useTranslation from 'next-translate/useTranslation'
import { dateFormater, hideCustomerName } from './helper'
import Link from '@/components/atoms/link'
import { Routes } from '@/core/routes'

type ReviewsDesktopProps = {
  review: ProductRateRow
  translatedReviews: TranslatedReviewsProps
  fetchTranslateReview: (reviewId: string) => void
  isOriginLanguage: originLanguageProps
  handleOriginLanguageClick: (reviewId: string) => void
}

const ReviewDesktop: FC<ReviewsDesktopProps> = ({
  review,
  translatedReviews,
  fetchTranslateReview,
  isOriginLanguage,
  handleOriginLanguageClick
}) => {
  const theme = useTheme()
  const { t } = useTranslation('common')
  const { subdomainName } = useDomain()
  const {
    customerName,
    createdAt,
    rate,
    description,
    id,
    showName,
    languageCode,
    colorValue,
    productId,
    sizeValue
  } = review ?? {}
  const isSubdomainLanguage = languageCode === subdomainName

  const handleTranslate = () => {
    if (!translatedReviews[id]) {
      fetchTranslateReview(id)
    }
    handleOriginLanguageClick(id)
  }

  return (
    <>
      <BaseInfoBox flexDirection="column">
        <StyledFlex alignItems="center">
          <Box>
            <ResponsiveImage
              imageUrl={applyImagePolicy(`/flags/${languageCode}.png`)}
              layout="fixed"
              width={16}
              height={11}
            />
          </Box>
          <Small3 m={0} mt="1px">
            {showName === true ? customerName : hideCustomerName(customerName)}
          </Small3>
        </StyledFlex>
        <ReviewDate>{dateFormater(createdAt)}</ReviewDate>
        <StyledFlex>
          <CheckIconFilledDefault />
          <StyledSmall3>
            {t('ProductDetail.Reviews.verifiedPurchase')}
          </StyledSmall3>
        </StyledFlex>
        <Flex flexDirection="column">
          {colorValue && (
            <StyledTiny2>
              {t('Category.Filter.color')}:
              {productId ? (
                <Link
                  href={`${Routes.product}/${productId}`}
                  textDecoration="underline"
                >
                  {colorValue}
                </Link>
              ) : (
                colorValue
              )}
            </StyledTiny2>
          )}
          {sizeValue && (
            <StyledTiny2>
              {t('Category.Filter.size')}:<span>{sizeValue}</span>
            </StyledTiny2>
          )}
        </Flex>
      </BaseInfoBox>
      <ReviewDescriptionBox>
        <Box>
          <Rating
            readonly
            initialValue={rate}
            allowFraction={true}
            size={14}
            fillColor={theme.colors.sizeTableDescription}
          />
        </Box>
        {description ? (
          <>
            <Description>
              {!isOriginLanguage[id]
                ? description
                : translatedReviews[id] || description}
            </Description>
            {!isOriginLanguage[id] && !isSubdomainLanguage && (
              <StyledButton onClick={handleTranslate}>
                {t('ProductDetail.Reviews.translate')}
              </StyledButton>
            )}
            {isOriginLanguage[id] && (
              <StyledButton onClick={() => handleOriginLanguageClick(id)}>
                {t('ProductDetail.Reviews.originalLanguage')}
              </StyledButton>
            )}
          </>
        ) : null}
      </ReviewDescriptionBox>
      <AdditionalRatingInfo productRateRow={review} inReview />
    </>
  )
}

export default ReviewDesktop

const BaseInfoBox = styled(Flex)`
  gap: 10px;
`

const ReviewDescriptionBox = styled(Flex)`
  flex-direction: column;
`

const Description = styled(Tiny2)`
  font-weight: 400;
`

const StyledButton = styled.button`
  border: none;
  background-color: inherit;
  text-decoration: underline;
  font-weight: 400;
  font-size: 12px;
  max-width: fit-content;
  padding: 0;
  cursor: pointer;
`

const StyledFlex = styled(Flex)`
  gap: 8px;
`

const ReviewDate = styled(Tiny2)`
  font-weight: 400;
  margin: 0;
  color: ${(props) => props.theme.colors.ratingReviewDate};
`

const StyledTiny2 = styled(Tiny2)`
  font-weight: 400;
  margin: 0;
  color: ${(props) => props.theme.colors.ratingReviewInfoNeutral};
  display: flex;
  gap: 2px;
`

const StyledSmall3 = styled(Small3)`
  font-weight: 500;
  margin: 0;
  color: ${(props) => props.theme.colors.ratingReviewVerified};
`
